import React, { useEffect, useState } from 'react';
import RequestFormLayout from 'components/layout/RequestFormLayout';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { notification } from 'antd';
import { useFormCleanup } from 'core/utils/useFormCleanup';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, CombinedStates } from 'core/types';
import useGuardingFlow from 'core/guardingRequestForm/useGuardingFlow';
import { deleteGuardingRequest, uploadGuardingRequestAttachment } from 'core/guardingRequestForm/guardingRequestFormApi';
import WorkTypeSelector from 'components/Form/WorkTypeSelector';
import WorkDetails from 'components/Form/WorkDetails';
import { CheckboxData } from 'components/Form/NestedCheckboxSearch/types';
import type { UploadFile } from 'antd/lib/upload/interface';
import { unwrapResult } from '@reduxjs/toolkit';
import { VisitRequestFormResponsePayload } from 'core/guardingRequestForm/types';
import { GUARDING_STEPPER_LABELS, GUARDING_STEPPER_LABELS_RETAIL } from 'core/utils/stepperLabels';
import { reportUnexpectedRejection } from 'core/utils/reportUnexpectedRejection';
import './jobDetailsForm.scss';

const GuardingJobDetailsForm: React.FC = () => {
    const navigation = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const { state: navState } = useLocation();
    const { stepData, visitRequest } = useSelector((state: CombinedStates) => state.guardingRequestForm);
    const isRetail = stepData?.isRetail || false;
    const [workDetails, setWorkDetails] = useState<CheckboxData[]>([]);
    const [workReference, setWorkReference] = useState<string>('');
    const [descriptionOfWorks, setDescriptionOfWorks] = useState<string>('');
    const [comments, setComments] = useState<string>('');
    const { id } = useParams<{ id: string }>();
    const flow = useGuardingFlow();
    const amending = navState?.amending;

    // Auto-cleanup request if user leaves without completing
    useFormCleanup({
        requestId: (!flow.guestMode && !amending) ? visitRequest.encryptedId : null,
        formBasePath: '/guarding-request/',
        deleteEndpoint: 'guarding-request',
        dispatchDelete: () => {
            if (visitRequest.encryptedId) dispatch(deleteGuardingRequest(visitRequest.encryptedId));
        },
    });

    const getSelectedWorkTypes = () => {
        const selectedParent = workDetails.find((workType) => workType.checked);
        if (!selectedParent) return [];

        // If parent has no children, the parent IS the work type
        if (!selectedParent.children || selectedParent.children.length === 0) {
            return [{ id: selectedParent.id, siteWorkTypeId: selectedParent.siteWorkTypeId }];
        }

        const selectedChild = selectedParent.children.find((child) => child.checked);
        return [{ id: selectedChild?.id }, { id: selectedParent.id, siteWorkTypeId: selectedParent.siteWorkTypeId }];
    };

    const handleNextClick = (navigationState: Record<string, unknown>) => {
        const selectedWorkTypes = getSelectedWorkTypes();
        const selectedParent = workDetails.find((workType) => workType.checked);
        let error = { message: '', description: '' };

        if (!selectedParent) {
            error = {
                message: 'Work Type Not Selected',
                description: 'Please select a work type to proceed',
            };
        } else if (selectedParent.children && selectedParent.children.length > 0) {
            const selectedChild = selectedParent.children.find((child) => child.checked);
            if (!selectedChild) {
                error = {
                    message: 'Work Type Not Selected',
                    description: 'Please select a child work type to proceed',
                };
            }
        }

        if (error.message === '' && selectedWorkTypes.some(workType => workType.id === undefined)) {
            error = {
                message: 'Work Type Not Selected',
                description: 'Please select a work type to proceed',
            };
        }

        if (error.message === '' && descriptionOfWorks === '') {
            error = {
                message: 'Description of Works is Required',
                description: 'Please provide a brief description of the works you will be completing',
            };
        }

        if (error.message === '' && workReference === '') {
            error = {
                message: 'Work Reference is Required',
                description: 'Please provide a reference for the work you will be completing',
            };
        }

        if (error.message !== '' && error.description !== '') {
            notification.error(error);
            return;
        }

        const collatedWorkDetails = {
            description: descriptionOfWorks,
            workReference,
            comments,
        };

        if (id !== undefined || flow.guestMode) {
            flow.updateStep(1, {
                workTypes: selectedWorkTypes,
                workDetails: collatedWorkDetails,
                amending,
                forcedStep: 1,
            }).then(unwrapResult).then((payload: VisitRequestFormResponsePayload) => {
                if (payload.stepData) {
                    // Retail: Job Details is the last step before Review; non-retail: first, so go to Date.
                    flow.goToStep(isRetail ? 4 : 2, navigationState);
                }
            }).catch(reportUnexpectedRejection);
        }
    };

    // Retail only: Job Details sits after Visitors, so back returns to Add Visitor (/step/3).
    const handleBackClick = (navigationState: Record<string, unknown>) => {
        flow.goToStep(3, navigationState);
    };

    const handleInitialLoad = async () => {
        if (id !== undefined || flow.guestMode) {
            const resultAction = await flow.loadStep(1, amending);

            // Redirect to dashboard if guarding request not found
            if (resultAction.meta.requestStatus === 'rejected') {
                // A guest has no dashboard and no way to create a request: send
                // them back to the link itself, which re-validates the token and
                // shows the invalid/expired message when that is the reason.
                if (flow.guestMode) {
                    navigation(`/booking/${flow.token}`, { replace: true });
                    return;
                }

                navigation('/dashboard', { replace: true });
                notification.error({
                    message: 'Guarding Request Not Found',
                    description: 'The guarding request could not be found. Please create a new request.',
                });
            }
        } else {
            navigation('/dashboard', { replace: true });
            notification.error({
                message: 'Invalid Guarding Request',
                description: 'Please select a guarding request to proceed',
            });
        }
    };

    useEffect(() => {
        handleInitialLoad();
    }, [id, amending]);

    useEffect(() => {
        if (descriptionOfWorks === '' && visitRequest.workDetails?.description) {
            setDescriptionOfWorks(visitRequest.workDetails?.description || '');
        }
        if (workReference === '' && visitRequest.workDetails?.workReference) {
            setWorkReference(visitRequest.workDetails?.workReference || '');
        }
        if (comments === '' && visitRequest.additionalComments) {
            setComments(visitRequest.additionalComments || '');
        }
    }, [visitRequest.workDetails, visitRequest.additionalComments]);

    useEffect(() => {
        if (stepData.workTypes) {
            const initialWorkTypes = stepData.workTypes.map(workType => ({
                ...workType,
                value: workType.id.toString(),
                checked: false,
                children: workType.children.map(siteWorkType => ({
                    ...siteWorkType,
                    value: siteWorkType.id.toString(),
                    checked: false,
                })),
            }));
            setWorkDetails(initialWorkTypes);

            if (visitRequest.workTypes) {
                const updatedWorkTypes = stepData.workTypes.map(workType => ({
                    ...workType,
                    value: workType.id.toString(),
                    checked: visitRequest.workTypes?.some((work) => work.workTypeId === workType.id) || false,
                    children: workType.children.map(siteWorkType => ({
                        ...siteWorkType,
                        value: siteWorkType.id.toString(),
                        checked: visitRequest.workTypes?.some((work) => work.workTypeId === siteWorkType.id) || false,
                    })),
                }));

                updatedWorkTypes.forEach((workType, index) => {
                    if (workType.children.length > 0) {
                        const childrenCount = workType.children.filter(child => child.checked).length;
                        if (childrenCount === workType.children.length) {
                            const updatedWorkType = { ...workType };
                            updatedWorkType.checked = true;
                            updatedWorkTypes[index] = updatedWorkType;
                        }
                    }
                });
                setWorkDetails(updatedWorkTypes);
            }
        }
    }, [stepData.workTypes]);

    const handleWorkTypeSelectorChange = (data: CheckboxData[]) => {
        setWorkDetails(data);
    };

    const handleAddAttachment = (file: UploadFile) => {
        if (id !== undefined) {
            dispatch(uploadGuardingRequestAttachment({ id, attachment: file, step: 'job_details' }));
        }
    };

    return (
        <RequestFormLayout
            currentStep={isRetail ? 3 : 1}
            isNextEnabled
            isBackEnabled={isRetail}
            {...(isRetail ? { onBackClick: handleBackClick } : {})}
            onNextClick={handleNextClick}
            stepperLabels={isRetail ? GUARDING_STEPPER_LABELS_RETAIL : GUARDING_STEPPER_LABELS}
            formTitle="Guarding Request Form"
            reduxSliceKey="guardingRequestForm">
            <>
                {visitRequest.site && (
                    <div className="site-details-box">
                        <div className="site-details-box__header">Site Details</div>
                        <div className="site-details-box__content">
                            <div className="site-details-box__col">
                                <span className="site-details-box__label">Site ID:</span>
                                <span className="site-details-box__value">{visitRequest.site.id}</span>
                            </div>
                            <div className="site-details-box__col">
                                <span className="site-details-box__label">Site Name:</span>
                                <span className="site-details-box__value">{visitRequest.site.name}</span>
                            </div>
                            <div className="site-details-box__col">
                                <span className="site-details-box__label">Address:</span>
                                <span className="site-details-box__value">{visitRequest.site.fullAddress}</span>
                            </div>
                        </div>
                    </div>
                )}
                <WorkTypeSelector workTypeData={workDetails} handleChange={handleWorkTypeSelectorChange} />
                {workDetails.length > 0 ? (
                    <WorkDetails
                        descriptionOfWorks={descriptionOfWorks}
                        workReference={workReference}
                        comments={comments}
                        onDescriptionOfWorksChange={(value) => setDescriptionOfWorks(value)}
                        onWorkReferenceChange={(value) => setWorkReference(value)}
                        onFileUploadChange={handleAddAttachment}
                        onCommentsChange={(value) => setComments(value)}
                        reduxSliceKey="guardingRequestForm" />
                ) : null}
            </>
        </RequestFormLayout>
    );
};

export default GuardingJobDetailsForm;
